import { showUserAlert } from "../utils/userErrorMessage";
// src/pages/SafetyNew.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../Component/Header";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { supabase } from "../supabase/client";

const ISSUE_OPTIONS = ["민원", "사고", "과태료"];

// 로컬 datetime-local → ISO(timestamptz)
function localDatetimeToISO(dtLocal) {
  if (!dtLocal) return null;
  const d = new Date(dtLocal);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export default function SafetyNew() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const company = localStorage.getItem("email") || "";

  const [saving, setSaving] = useState(false);

  // 드라이버 목록: safety.jsx에서 넘겨줬으면 사용, 없으면 profiles에서 로드
  const [drivers, setDrivers] = useState(() =>
    Array.isArray(state?.drivers) ? state.drivers : []
  );
  const [selectedDriver, setSelectedDriver] = useState("");

  // 운전자 차량 후보 & 선택
  const [carOptions, setCarOptions] = useState([]);
  const [carNumber, setCarNumber] = useState("");

  // 폼
  const [form, setForm] = useState({
    issue: "",        // 민원/사고/과태료
    dateLocal: "",
    place: "",
    opponent: "",
    summary: "",
    // 사고/과태료 전용
    insurance: "",
    process: "",
    etc: "",
    surcharge: "",
  });

  const isAccidentLike = useMemo(
    () => form.issue === "사고" || form.issue === "과태료",
    [form.issue]
  );

  // drivers 불러오기
  useEffect(() => {
    const fetchDriversIfNeeded = async () => {
      if (drivers.length > 0 || !company) return;
      const { data, error } = await supabase
        .from("profiles")
        .select("driver_name")
        .eq("company", company)
        .eq("role", "driver");
      if (!error) {
        const names = (data || [])
          .map((r) => r?.driver_name)
          .filter(Boolean)
          .filter((v, i, arr) => arr.indexOf(v) === i);
        setDrivers(names);
      }
    };
    fetchDriversIfNeeded();
  }, [company, drivers.length]);

  // 운전자 선택 시 차량 로드
  useEffect(() => {
    const loadCars = async () => {
      setCarOptions([]);
      setCarNumber("");
      if (!selectedDriver || !company) return;

      const { data, error } = await supabase
        .from("cars")
        .select("car_number, car_number_only, driver")
        .eq("company", company)
        .eq("driver", selectedDriver);

      if (!error) {
        const list = (data || []).map((c) => ({
          car_number: c.car_number_only || "",
          car_number_only: c.car_number_only || "",
        })).filter(c => !!c.car_number || !!c.car_number_only);
        setCarOptions(list);
        if (list.length === 1) setCarNumber(list[0].car_number || list[0].car_number_only);
      }
    };
    loadCars();
  }, [selectedDriver, company]);

  const canSave = useMemo(() => {
    return (
      company &&
      form.issue &&
      selectedDriver &&
      (carNumber || carOptions.length > 0) &&
      form.dateLocal &&
      form.summary.trim()
    );
  }, [company, form.issue, selectedDriver, carNumber, carOptions.length, form.dateLocal, form.summary]);

  const handleSubmit = async () => {
    if (saving) return;
    if (!canSave) {
      showUserAlert("필수 항목(형태/운전자/일시/개요 등)을 확인하세요.");
      return;
    }
    setSaving(true);
    try {
      const dateISO = localDatetimeToISO(form.dateLocal);
      if (!dateISO) throw new Error("일시 형식을 확인해 주세요.");

      const payload = {
        company,
        company_name: localStorage.getItem("companyName") || company,
        driver_name: selectedDriver,
        car_number: carNumber || null,
        issue: form.issue,
        date: dateISO,               // timestamptz
        place: form.place || null,
        // 민원일 때 상대 차량은 수집하지 않음
        opponent: form.issue === "민원" ? null : (form.opponent || null),
        summary: form.summary || null,
        // 사고/과태료일 때만 채움
        insurance: isAccidentLike ? (form.insurance || null) : null,
        process: isAccidentLike ? (form.process || null) : null,
        etc: isAccidentLike ? (form.etc || null) : null,
        surcharge: isAccidentLike ? (form.surcharge || null) : null,
      };

      const { error } = await supabase.from("safety").insert([payload]);
      if (error) throw error;

      showUserAlert("안전 보고서가 등록되었습니다.");
      navigate("/safety");
    } catch (e) {
      showUserAlert("등록 실패: " + (e?.message || e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-1">
      <Header />

      <div className="px-4 pt-1 pb-4 space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">안전 보고서</h2>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(-1)}>취소</Button>
            <Button onClick={handleSubmit} disabled={!canSave || saving}>
              {saving ? "등록중..." : "등록"}
            </Button>
          </div>
        </div>

        <Card className="p-2">
          <CardContent className="bg-white safety-report-form space-y-2">
            <div className="safety-report-section-title">기본 정보</div>
            {/* 형태(issue) & 운전자 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              <div>
                <label className="block text-sm font-semibold mb-1">형태</label>
                <select
                  className="border rounded px-3 py-2 w-full"
                  value={form.issue}
                  onChange={(e) => setForm((p) => ({ ...p, issue: e.target.value }))}
                >
                  <option value="">선택</option>
                  {ISSUE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1">운전자</label>
                <select
                  className="border rounded px-3 py-2 w-full"
                  value={selectedDriver}
                  onChange={(e) => setSelectedDriver(e.target.value)}
                >
                  <option value="">선택</option>
                  {drivers.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* 차량 & 일시 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              <div>
                <label className="block text-sm font-semibold mb-1">자기 차량</label>
                {carOptions.length <= 1 ? (
                  <Input value={carNumber} onChange={(e) => setCarNumber(e.target.value)} placeholder="자동 채움/직접 입력" />
                ) : (
                  <select
                    className="border rounded px-3 py-2 w-full"
                    value={carNumber}
                    onChange={(e) => setCarNumber(e.target.value)}
                  >
                    <option value="">선택</option>
                    {carOptions.map((c) => {
                      const v = c.car_number || c.car_number_only;
                      return <option key={v} value={v}>{v}</option>;
                    })}
                  </select>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1">일시</label>
                <input
                  type="datetime-local"
                  className="border rounded px-3 py-2 w-full"
                  value={form.dateLocal}
                  onChange={(e) => setForm((p) => ({ ...p, dateLocal: e.target.value }))}
                />
              </div>
            </div>

            <div className="safety-report-section-title">장소 및 개요</div>
            {/* 장소 / 상대 차량(민원일 때 숨김) */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              <div>
                <label className="block text-sm font-semibold mb-1">장소</label>
                <Input
                  value={form.place}
                  onChange={(e) => setForm((p) => ({ ...p, place: e.target.value }))}
                  placeholder="예: 서울역 앞"
                />
              </div>

              {form.issue !== "민원" && (
                <div>
                  <label className="block text-sm font-semibold mb-1">상대 차량</label>
                  <Input
                    value={form.opponent}
                    onChange={(e) => setForm((p) => ({ ...p, opponent: e.target.value }))}
                    placeholder="예: 12가3456 / 버스 / 택시 등"
                  />
                </div>
              )}
            </div>

            {/* 개요 */}
            <div className="safety-report-summary">
              <label className="block text-sm font-semibold mb-1">개요</label>
              <textarea
                className="w-full border rounded px-3 py-2"
                value={form.summary}
                onChange={(e) => setForm((p) => ({ ...p, summary: e.target.value }))}
                placeholder="사건 개요를 자세히 입력하세요."
              />
            </div>

            {/* 사고/과태료 전용 추가 정보 */}
            {isAccidentLike && (
              <>
                <div className="safety-report-section-title">처리 정보</div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                  <div>
                    <label className="block text-sm font-semibold mb-1">보험</label>
                    <Input
                      value={form.insurance}
                      onChange={(e) => setForm((p) => ({ ...p, insurance: e.target.value }))}
                      placeholder="예: ○○보험 전화접수 / 접수번호 등"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1">처리</label>
                    <Input
                      value={form.process}
                      onChange={(e) => setForm((p) => ({ ...p, process: e.target.value }))}
                      placeholder="예: 합의 / 수리 / 접수 후 진행중 등"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1">비고</label>
                    <Input
                      value={form.etc}
                      onChange={(e) => setForm((p) => ({ ...p, etc: e.target.value }))}
                      placeholder="추가 메모"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1">지급금액</label>
                    <Input
                      value={form.surcharge}
                      onChange={(e) => setForm((p) => ({ ...p, surcharge: e.target.value }))}
                      placeholder="예: 200,000"
                    />
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
